import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { message } from 'antd';
import { apiService } from '../../services/api';

export const DEFAULT_LTE_BAND_OPTIONS = [
  { value: 'B1', label: 'B1', disabled: false },
  { value: 'B3', label: 'B3', disabled: false },
  { value: 'B7', label: 'B7', disabled: false },
  {
    value: 'B20',
    label: 'B20',
    disabled: true,
    disabledReason: 'может уйти в 3G',
  },
];

export const LTE_METRIC_THRESHOLDS = {
  snr: { excellent: 20, good: 13, label: 'отлично ≥ 20' },
  rsrq: { excellent: -10, good: -14, label: 'отлично ≥ -10' },
  rsrp: { excellent: -80, good: -85, label: 'отлично ≥ -80 (хорошо ≥ -85)' },
};

/** Пороги скорости (Мбит/с) для графиков проверки локаций. */
export const SPEED_METRIC_THRESHOLDS = {
  download: { excellent: 25, good: 15, label: 'отлично ≥ 25 (среднее ≥ 15)' },
  upload: { excellent: 25, good: 15, label: 'отлично ≥ 25 (среднее ≥ 15)' },
};

export function parseMetricNumber(raw) {
  if (raw === null || raw === undefined || raw === '') return null;
  const n = Number(String(raw).replace(',', '.'));
  return Number.isFinite(n) ? n : null;
}

/** @returns {'ok'|'warn'|'bad'|'na'} */
export function metricQuality(kind, raw) {
  const value = parseMetricNumber(raw);
  if (value === null) return 'na';
  const t = LTE_METRIC_THRESHOLDS[kind] || SPEED_METRIC_THRESHOLDS[kind];
  if (!t) return 'na';
  if (value >= t.excellent) return 'ok';
  if (value >= t.good) return 'warn';
  return 'bad';
}

function formatLockDisplay(metrics) {
  if (!metrics) return '—';
  const hex = metrics.lte_band_lock_hex;
  const raw = metrics.lte_band_lock;
  const bands = Array.isArray(metrics.bands_enabled) ? metrics.bands_enabled.join('+') : '';
  if (hex || raw) {
    return `${hex || raw}${bands ? ` (${bands})` : ''}`;
  }
  return bands || '—';
}

/** HTTPException с dict detail у нас отдаётся без обёртки {detail: ...} — см. main.http_exception_handler. */
export function extractApiError(err, fallback = 'Ошибка запроса') {
  const data = err?.response?.data;
  let detail = data?.detail;
  if (detail == null && data && typeof data === 'object' && (data.message || data.error)) {
    detail = data;
  }
  if (typeof detail === 'string') {
    try {
      const parsed = JSON.parse(detail);
      if (parsed && typeof parsed === 'object') detail = parsed;
      else return detail;
    } catch {
      return detail;
    }
  }
  if (detail && typeof detail === 'object') {
    return detail.message || detail.error || JSON.stringify(detail);
  }
  if (typeof data?.message === 'string') return data.message;
  if (err?.response?.status) {
    return `HTTP ${err.response.status}: ${err.message || fallback}`;
  }
  return err?.message || fallback;
}

/** LTE Band / RF goform — только ZTE (как SUPPORTED_MODEM_PREFIXES на бэке). */
export function isLteBandSupportedModemModel(modelName) {
  const name = String(modelName || '').trim();
  if (!name || name.toLowerCase() === 'unknown') return null; // неизвестно — пусть решит API
  return /^zte\b/i.test(name);
}

/**
 * Секция LTE Band для PortEdit / PortBulkEdit (pcp__ стили).
 * Apply — отдельная кнопка (не на blur/save формы порта).
 */
const LteBandSection = ({
  modemId = null,
  modemIds = null,
  bulk = false,
  visible = false,
  /** После успешного apply: обновить колонку Band в таблице портов / сбросить кэш. */
  onApplied = null,
}) => {
  const [selectedBands, setSelectedBands] = useState(['B1', 'B3', 'B7']);
  const [metrics, setMetrics] = useState(null);
  const [loadingMetrics, setLoadingMetrics] = useState(false);
  const [applying, setApplying] = useState(false);
  const [error, setError] = useState(null);
  const pollRef = useRef(null);
  const genRef = useRef(0);
  const applyingRef = useRef(false);

  const effectiveModemId = useMemo(() => {
    if (modemId != null && Number(modemId) > 0) return Number(modemId);
    return null;
  }, [modemId]);

  const bulkIds = useMemo(() => {
    if (!Array.isArray(modemIds)) return [];
    const seen = new Set();
    const out = [];
    modemIds.forEach((id) => {
      const n = Number(id);
      if (!Number.isFinite(n) || n <= 0 || seen.has(n)) return;
      seen.add(n);
      out.push(n);
    });
    return out;
  }, [modemIds]);

  const bandOptions = useMemo(() => {
    const fromApi = metrics?.available_bands;
    if (Array.isArray(fromApi) && fromApi.length) {
      return fromApi.map((item) => ({
        value: item.band,
        label: item.band,
        disabled: Boolean(item.disabled),
        disabledReason: item.disabled_reason || undefined,
      }));
    }
    return DEFAULT_LTE_BAND_OPTIONS;
  }, [metrics]);

  const loadMetrics = useCallback(async ({ syncSelection = true } = {}) => {
    if (bulk || !effectiveModemId) return;
    // Не дергать goform параллельно с SET — даёт ложный 409 / пустой lock
    if (applyingRef.current) return;
    const gen = ++genRef.current;
    setLoadingMetrics(true);
    setError(null);
    try {
      const res = await apiService.getModemRadioMetrics(effectiveModemId);
      if (gen !== genRef.current) return;
      const data = res?.data?.data || res?.data || null;
      setMetrics(data);
      if (!syncSelection) return;
      if (Array.isArray(data?.bands_enabled) && data.bands_enabled.length) {
        setSelectedBands(data.bands_enabled.filter((b) => b !== 'B20'));
      } else if (Array.isArray(data?.allowed_lte_bands) && data.allowed_lte_bands.length) {
        setSelectedBands(data.allowed_lte_bands.filter((b) => b !== 'B20'));
      }
    } catch (err) {
      if (gen !== genRef.current) return;
      setError(extractApiError(err));
    } finally {
      if (gen === genRef.current) setLoadingMetrics(false);
    }
  }, [bulk, effectiveModemId]);

  useEffect(() => {
    if (!visible) {
      genRef.current += 1;
      setMetrics(null);
      setError(null);
      setLoadingMetrics(false);
      setApplying(false);
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
      return undefined;
    }

    if (!bulk && effectiveModemId) {
      loadMetrics();
      pollRef.current = setInterval(() => {
        loadMetrics();
      }, 60000);
    }

    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
  }, [visible, bulk, effectiveModemId, loadMetrics]);

  const toggleBand = useCallback((band, disabled) => {
    if (disabled) return;
    setSelectedBands((prev) => {
      if (prev.includes(band)) {
        if (prev.length <= 1) return prev;
        return prev.filter((b) => b !== band);
      }
      return [...prev, band];
    });
  }, []);

  const handleApply = useCallback(async () => {
    if (!selectedBands.length) {
      message.warning('Выберите хотя бы один LTE band');
      return;
    }
    applyingRef.current = true;
    setApplying(true);
    setError(null);
    try {
      if (bulk) {
        if (!bulkIds.length) {
          message.warning('Нет выбранных модемов');
          return;
        }
        const res = await apiService.setModemsBandPolicyBulk(bulkIds, selectedBands);
        const data = res?.data?.data || res?.data || {};
        const ok = data.success_count ?? 0;
        const fail = data.error_count ?? 0;
        if (fail > 0) {
          message.warning(`Band применён: ${ok} ок, ${fail} с ошибкой`);
        } else {
          message.success(`Band применён к ${ok} модемам`);
        }
        if (typeof onApplied === 'function' && ok > 0) {
          try {
            await onApplied(data);
          } catch {
            // таблица обновится при следующем fetch
          }
        }
      } else {
        if (!effectiveModemId) {
          message.warning('Нет modem_id порта');
          return;
        }
        const res = await apiService.setModemBandPolicy(effectiveModemId, selectedBands);
        const data = res?.data?.data || res?.data || null;
        setMetrics(data);
        message.success(
          `Band lock: ${(data?.bands_enabled || data?.allowed_lte_bands || selectedBands).join('+') || 'ok'}`,
        );
        if (data?.wan_connect_status && !String(data.wan_connect_status).toLowerCase().includes('connect')) {
          message.warning(`Статус: ${data.wan_connect_status}`);
        }
        if (typeof onApplied === 'function' && data) {
          try {
            await onApplied(data);
          } catch {
            // таблица обновится при следующем fetch
          }
        }
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const code = detail && typeof detail === 'object' ? detail.error : null;
      const msg = extractApiError(err);
      // lock уже мог примениться — перечитаем метрики и смягчим UX
      if (!bulk && effectiveModemId && code === 'lock_not_applied') {
        applyingRef.current = false;
        await loadMetrics({ syncSelection: false });
      }
      setError(msg);
      message.error(msg);
    } finally {
      applyingRef.current = false;
      setApplying(false);
    }
  }, [bulk, bulkIds, effectiveModemId, loadMetrics, onApplied, selectedBands]);

  const snrQ = metricQuality('snr', metrics?.lte_snr);
  const rsrqQ = metricQuality('rsrq', metrics?.lte_rsrq);
  const rsrpQ = metricQuality('rsrp', metrics?.lte_rsrp);

  return (
    <div className="pcp__section">
      <div className="pcp__section-title">LTE Band</div>

      {!bulk && (
        <div className="pcp__row">
          <div className="pcp__field">
            <div className="pcp__label">
              <span className="pcp__label-text">Active band</span>
            </div>
            <div className="pcp__select-trigger pcp__select-trigger--disabled">
              <span className="pcp__select-value">
                {loadingMetrics && !metrics
                  ? 'Загрузка…'
                  : (metrics?.wan_active_band || '—')}
              </span>
            </div>
          </div>
          <div className="pcp__field">
            <div className="pcp__label">
              <span className="pcp__label-text">Lock mask</span>
            </div>
            <div className="pcp__select-trigger pcp__select-trigger--disabled">
              <span className="pcp__select-value">{formatLockDisplay(metrics)}</span>
            </div>
          </div>
        </div>
      )}

      {bulk && (
        <p className="pcp__subsection-status" role="note">
          Политика band будет применена к {bulkIds.length} выбранным модемам (~5с downtime на модем).
        </p>
      )}

      <div className="pcp__field">
        <div className="pcp__label">
          <span className="pcp__label-text">Разрешённые band</span>
        </div>
        <div className="pcp__band-checks" role="group" aria-label="LTE bands">
          {bandOptions.map((opt) => {
            const checked = selectedBands.includes(opt.value);
            const disabled = Boolean(opt.disabled);
            return (
              <label
                key={opt.value}
                className={`pcp__band-check${disabled ? ' pcp__band-check--disabled' : ''}${checked ? ' pcp__band-check--on' : ''}`}
                title={disabled ? (opt.disabledReason || 'Недоступно') : undefined}
              >
                <input
                  type="checkbox"
                  checked={checked}
                  disabled={disabled || applying}
                  onChange={() => toggleBand(opt.value, disabled)}
                  aria-label={opt.label}
                />
                <span>{opt.label}</span>
                {disabled && opt.disabledReason ? (
                  <span className="pcp__band-check-hint">{opt.disabledReason}</span>
                ) : null}
              </label>
            );
          })}
        </div>
      </div>

      <div className="pcp__row">
        <button
          type="button"
          className="pcp__btn pcp__btn--submit pcp__btn--band-apply"
          onClick={handleApply}
          disabled={applying || (!bulk && !effectiveModemId) || (bulk && !bulkIds.length)}
          aria-label="Применить band"
          tabIndex={0}
        >
          {applying ? 'Применение…' : 'Применить band'}
        </button>
      </div>

      {error && (
        <p className="pcp__subsection-status pcp__subsection-status--err" role="alert">
          {error}
        </p>
      )}

      {!bulk && (
        <div className="pcp__subsection pcp__band-metrics">
          <div className="pcp__subsection-title">Метрики RF</div>
          {loadingMetrics && !metrics ? (
            <p className="pcp__subsection-status" role="status">Загрузка метрик…</p>
          ) : (
            <div className="pcp__metrics-grid">
              <div className={`pcp__metric pcp__metric--${snrQ}`}>
                <div className="pcp__metric-label">SNR</div>
                <div className="pcp__metric-value">{metrics?.lte_snr ?? '—'}</div>
                <div className="pcp__metric-hint">{LTE_METRIC_THRESHOLDS.snr.label}</div>
              </div>
              <div className={`pcp__metric pcp__metric--${rsrqQ}`}>
                <div className="pcp__metric-label">RSRQ</div>
                <div className="pcp__metric-value">{metrics?.lte_rsrq ?? '—'}</div>
                <div className="pcp__metric-hint">{LTE_METRIC_THRESHOLDS.rsrq.label}</div>
              </div>
              <div className={`pcp__metric pcp__metric--${rsrpQ}`}>
                <div className="pcp__metric-label">RSRP</div>
                <div className="pcp__metric-value">{metrics?.lte_rsrp ?? '—'}</div>
                <div className="pcp__metric-hint">{LTE_METRIC_THRESHOLDS.rsrp.label}</div>
              </div>
            </div>
          )}
          {metrics?.wan_connect_status ? (
            <p className="pcp__subsection-status" role="status">
              Status: {metrics.wan_connect_status}
              {metrics.network_type ? ` · ${metrics.network_type}` : ''}
            </p>
          ) : null}
        </div>
      )}
    </div>
  );
};

export default LteBandSection;
